import { useState, useEffect } from 'react'
import {
  getSubscriptionLimits,
  getUsageStats,
  getSubscriptionStatus,
  getFeatureAccess,
  getTrialDaysRemaining,
  canAddMember,
  canAddAdmin,
  canSendSMS,
  SubscriptionLimits,
  UsageStats,
  SubscriptionStatus,
} from '@/lib/subscriptionService'

interface UseSubscriptionResult {
  limits: SubscriptionLimits | null
  usage: UsageStats | null
  status: SubscriptionStatus | null
  trialDaysRemaining: number
  features: {
    finance: boolean
    attendance: boolean
    advancedMinistry: boolean
    completeFinance: boolean
    qrAttendance: boolean
    ministryScheduling: boolean
    hrPayroll: boolean
    mediaLibrary: boolean
    mobileApp: boolean
    apiAccess: boolean
    multiLocation: boolean
  }
  loading: boolean
  error: string | null
  refresh: () => Promise<void>
}

/**
 * Hook to get subscription information for a church
 */
export function useSubscription(churchId: string | null | undefined): UseSubscriptionResult {
  const [limits, setLimits] = useState<SubscriptionLimits | null>(null)
  const [usage, setUsage] = useState<UsageStats | null>(null)
  const [status, setStatus] = useState<SubscriptionStatus | null>(null)
  const [trialDaysRemaining, setTrialDaysRemaining] = useState(0)
  const [features, setFeatures] = useState({
    finance: false,
    attendance: false,
    advancedMinistry: false,
    completeFinance: false,
    qrAttendance: false,
    ministryScheduling: false,
    hrPayroll: false,
    mediaLibrary: false,
    mobileApp: false,
    apiAccess: false,
    multiLocation: false,
  })
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)

  async function loadSubscriptionData() {
    if (!churchId) {
      setLoading(false)
      return
    }

    try {
      setLoading(true)
      setError(null)

      const [limitsData, usageData, statusData, featuresData, trialDays] = await Promise.all([
        getSubscriptionLimits(churchId),
        getUsageStats(churchId),
        getSubscriptionStatus(churchId),
        getFeatureAccess(churchId),
        getTrialDaysRemaining(churchId),
      ])

      setLimits(limitsData)
      setUsage(usageData)
      setStatus(statusData)
      setFeatures(featuresData)
      setTrialDaysRemaining(trialDays)
    } catch (err) {
      console.error('Error loading subscription data:', err)
      setError(err instanceof Error ? err.message : 'Failed to load subscription data')
    } finally {
      setLoading(false)
    }
  }

  useEffect(() => {
    loadSubscriptionData()
  }, [churchId])

  return {
    limits,
    usage,
    status,
    trialDaysRemaining,
    features,
    loading,
    error,
    refresh: loadSubscriptionData,
  }
}

/**
 * Hook to check if a member can be added
 */
export function useCanAddMember(churchId: string | null | undefined) {
  const [canAdd, setCanAdd] = useState(false)
  const [checking, setChecking] = useState(true)

  useEffect(() => {
    async function check() {
      if (!churchId) {
        setChecking(false)
        return
      }

      setChecking(true)
      const result = await canAddMember(churchId)
      setCanAdd(result)
      setChecking(false)
    }

    check()
  }, [churchId])

  return { canAdd, checking }
}

/**
 * Hook to check if an admin can be added
 */
export function useCanAddAdmin(churchId: string | null | undefined) {
  const [result, setResult] = useState({ canAdd: false, current: 0, max: 0 })
  const [checking, setChecking] = useState(true)

  useEffect(() => {
    async function check() {
      if (!churchId) {
        setChecking(false)
        return
      }

      setChecking(true)
      const data = await canAddAdmin(churchId)
      setResult(data)
      setChecking(false)
    }

    check()
  }, [churchId])

  return { ...result, checking }
}

/**
 * Hook to check if SMS can be sent
 */
export function useCanSendSMS(churchId: string | null | undefined, count: number = 1) {
  const [result, setResult] = useState({ canSend: false, remaining: 0 })
  const [checking, setChecking] = useState(true)

  useEffect(() => {
    async function check() {
      if (!churchId) {
        setChecking(false)
        return
      }

      setChecking(true)
      const data = await canSendSMS(churchId, count)
      setResult(data)
      setChecking(false)
    }

    check()
  }, [churchId, count])

  return { ...result, checking }
}
